import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import api from "../services/api"
import { appPath, apiPath } from "../config/paths"
import logoUrl from "../logo.webp"

export default function NDA() {
    const navigate = useNavigate()
    const [user, setUser] = useState(null)
    const [isCheckingAccess, setIsCheckingAccess] = useState(true)
    const [canAccessNda, setCanAccessNda] = useState(false)
    const [fullName, setFullName] = useState("")
    const [debouncedFullName, setDebouncedFullName] = useState("")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [previewUrl, setPreviewUrl] = useState(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [previewError, setPreviewError] = useState(null)
    const [submitError, setSubmitError] = useState("")
    const [agreed, setAgreed] = useState(false)

    useEffect(() => {
        let cancelled = false
        const checkNdaAccess = async () => {
            try {
                const res = await api.get("/auth/me")
                if (cancelled) return
                const currentUser = res.data
                setUser(currentUser)
                localStorage.setItem("user", JSON.stringify(currentUser))
                if (currentUser.firstName && currentUser.lastName) {
                    const initialName = `${currentUser.firstName} ${currentUser.lastName}`
                    setFullName(initialName)
                    setDebouncedFullName(initialName)
                }
                const shouldShowNda = currentUser.role === "prospect" && !currentUser.ndaSigned
                if (!shouldShowNda) { navigate("/", { replace: true }); return }
                setCanAccessNda(true)
            } catch {
                localStorage.removeItem("user")
                if (!cancelled) navigate("/", { replace: true })
            } finally {
                if (!cancelled) setIsCheckingAccess(false)
            }
        }
        checkNdaAccess()
        return () => { cancelled = true }
    }, [navigate])

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedFullName(fullName), 800)
        return () => clearTimeout(timer)
    }, [fullName])

    useEffect(() => {
        if (!user?.email) return
        setPreviewLoading(true)
        setPreviewError(null)
        const params = new URLSearchParams({ fullName: debouncedFullName })
        setPreviewUrl(`${apiPath(`/nda/preview?${params.toString()}`)}#toolbar=0`)
    }, [user?.email, debouncedFullName])

    const handleBack = () => {
        navigate("/")
    }

    const submit = async (e) => {
        e.preventDefault()
        setSubmitError("")
        if (!user) { setSubmitError("Session not found. Please refresh the page."); return }
        if (!fullName.trim()) { setSubmitError("Please enter your full name for the legal e-signature."); return }
        if (!agreed) { setSubmitError("Please confirm that you have read and agree to the NDA."); return }
        if (isSubmitting) return
        setIsSubmitting(true)
        try {
            await api.post("/nda/submit", { email: user.email, fullName })
            navigate("/")
        } catch {
            setSubmitError("Failed to submit NDA. Please try again.")
        } finally {
            setIsSubmitting(false)
        }
    }

    if (isCheckingAccess) {
        return (
            <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
                    <div className="nda-spinner" />
                    <span style={{ fontSize: "14px", color: "var(--text-3)", fontFamily: "var(--font)" }}>Checking NDA access…</span>
                </div>
            </div>
        )
    }

    if (!canAccessNda) return null

    const canSubmit = agreed && fullName.trim() && !isSubmitting

    return (
        <div style={{ height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden", background: "var(--bg)" }}>

            {/* ── HEADER — same as landing page ── */}
            <header className="header" style={{ flexShrink: 0 }}>
                <div className="header-inner">
                    <a href={appPath("/")} className="logo" style={{ textDecoration: "none" }}>
                        <img src={logoUrl} alt="Darwinbox" className="logo-img" />
                        <div className="logo-divider" />
                        <span className="logo-tag">Trust Center</span>
                    </a>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <button
                            type="button"
                            onClick={handleBack}
                            style={{
                                background: "transparent", border: "1px solid var(--border)",
                                borderRadius: "var(--r-pill)", padding: "6px 14px",
                                fontSize: "12px", fontWeight: "600", color: "var(--text-2)",
                                cursor: "pointer", fontFamily: "var(--font)",
                            }}
                        >
                            Back
                        </button>
                        <span style={{
                            fontSize: "11px", fontWeight: "700", letterSpacing: "0.7px",
                            textTransform: "uppercase", color: "var(--blue-accent)",
                            background: "var(--surface-2)", padding: "4px 12px",
                            borderRadius: "var(--r-pill)", border: "1px solid #bfdbfe",
                        }}>Confidential</span>
                    </div>
                </div>
            </header>

            {/* ── SUBHEADER — matches hero/sec-tab style ── */}
            <div style={{
                flexShrink: 0,
                background: "linear-gradient(135deg, var(--navy) 0%, var(--navy-light) 100%)",
                padding: "18px 32px",
                display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
                <div>
                    <h1 style={{
                        fontFamily: "var(--font-display)", fontWeight: "800",
                        fontSize: "20px", color: "#ffffff", margin: "0 0 3px",
                        letterSpacing: "-0.3px",
                    }}>Sign Non-Disclosure Agreement</h1>
                    <p style={{ fontSize: "13px", color: "rgba(255,255,255,0.55)", margin: 0, fontFamily: "var(--font)" }}>
                        Review the document carefully and provide your legal e-signature to proceed
                    </p>
                </div>
                {user && (
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
                        <div style={{
                            width: "34px", height: "34px", borderRadius: "50%",
                            background: "rgba(255,255,255,0.15)", border: "1.5px solid rgba(255,255,255,0.25)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: "13px", fontWeight: "700", color: "#fff",
                        }}>
                            {user.email?.[0]?.toUpperCase() || "U"}
                        </div>
                        <div>
                            <p style={{ fontSize: "13px", fontWeight: "600", color: "#fff", margin: 0 }}>
                                {user.firstName} {user.lastName}
                            </p>
                            <p style={{ fontSize: "11px", color: "rgba(255,255,255,0.5)", margin: 0 }}>{user.email}</p>
                        </div>
                    </div>
                )}
            </div>

            {/* ── TWO-COLUMN BODY ── */}
            <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>

                {/* ─── LEFT: PDF VIEWER ─── */}
                <div style={{
                    flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
                    background: "#2d2d2d", position: "relative",
                }}>
                    {/* PDF toolbar */}
                    <div style={{
                        height: "44px", flexShrink: 0,
                        background: "#1c1c1c",
                        borderBottom: "1px solid rgba(255,255,255,0.07)",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "0 20px",
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <svg width="16" height="16" viewBox="0 0 40 41" fill="none">
                                <path d="M4.42553 40.8511H25.5319C21.7872 38.8085 19.234 34.8936 19.234 30.2979C19.234 23.6596 24.6809 18.2128 31.3192 18.2128H31.4894V4.42553C31.4894 2.04255 29.4468 0 27.0638 0H4.42553C2.04255 0 0 2.04255 0 4.42553V36.4255C0 38.8085 1.87234 40.8511 4.42553 40.8511ZM8.34043 6.80851H22.9787C24 6.80851 24.6809 7.65957 24.6809 8.51064C24.6809 9.53192 24 10.2128 22.9787 10.2128H8.34043C7.31915 10.2128 6.6383 9.53192 6.6383 8.51064C6.6383 7.65957 7.48936 6.80851 8.34043 6.80851ZM8.34043 14.8085H17.8723C18.8936 14.8085 19.5745 15.6596 19.5745 16.5106C19.5745 17.5319 18.8936 18.2128 17.8723 18.2128H8.34043C7.31915 18.2128 6.6383 17.5319 6.6383 16.5106C6.6383 15.4894 7.48936 14.8085 8.34043 14.8085Z" fill="#0183FF"/>
                                <path d="M31.3176 21.7872C26.5516 21.7872 22.6367 25.7021 22.6367 30.4681C22.6367 35.234 26.5516 39.1489 31.3176 39.1489C36.0835 39.1489 39.9984 35.234 39.9984 30.4681C39.9984 25.7021 36.0835 21.7872 31.3176 21.7872ZM35.5729 29.617L31.3176 33.7021C30.9771 34.0425 30.6367 34.2128 30.1261 34.2128C29.6154 34.2128 29.275 34.0425 28.9346 33.7021L26.892 31.4894C26.2112 30.8085 26.2112 29.7872 27.0623 29.1064C27.7431 28.4255 28.7644 28.4255 29.4452 29.1064L30.2963 30.1277L33.3601 27.234C34.041 26.5532 35.0622 26.5532 35.7431 27.234C36.2537 27.7447 36.2537 28.9362 35.5729 29.617Z" fill="#0183FF"/>
                            </svg>
                            <span style={{ fontSize: "12px", fontWeight: "600", color: "rgba(255,255,255,0.65)", fontFamily: "var(--font)" }}>
                                Non-Disclosure Agreement
                            </span>
                        </div>
                        <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.3)", fontFamily: "var(--font)" }}>
                            Scroll through all pages before signing
                        </span>
                    </div>

                    {/* PDF content */}
                    <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
                        {previewLoading && (
                            <div style={{
                                position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                                alignItems: "center", justifyContent: "center", gap: "14px",
                                background: "#2d2d2d", zIndex: 2,
                            }}>
                                <div className="nda-spinner" style={{ borderColor: "rgba(255,255,255,0.1)", borderTopColor: "#0183FF" }} />
                                <span style={{ fontSize: "13px", color: "rgba(255,255,255,0.35)", fontFamily: "var(--font)" }}>Loading document…</span>
                            </div>
                        )}
                        {previewError && !previewLoading && (
                            <div style={{
                                position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                                alignItems: "center", justifyContent: "center", gap: "14px",
                                background: "#2d2d2d", zIndex: 2,
                            }}>
                                <span style={{ fontSize: "13px", color: "rgba(255,255,255,0.45)", textAlign: "center", maxWidth: "300px", fontFamily: "var(--font)", lineHeight: "1.6" }}>
                                    {previewError}
                                </span>
                            </div>
                        )}
                        {previewUrl && (
                            <iframe
                                key={previewUrl}
                                src={previewUrl}
                                style={{ width: "100%", height: "100%", border: "none", display: "block" }}
                                title="NDA Document"
                                onLoad={() => {
                                    setPreviewLoading(false)
                                    setPreviewError(null)
                                }}
                                onError={() => {
                                    setPreviewLoading(false)
                                    setPreviewError("Failed to load document preview. Please try refreshing.")
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* ─── RIGHT: SIGN PANEL ─── */}
                <div style={{
                    width: "400px", flexShrink: 0,
                    background: "var(--white)",
                    borderLeft: "1px solid var(--border)",
                    display: "flex", flexDirection: "column",
                    overflowY: "auto",
                    boxShadow: "-4px 0 20px rgba(0,43,84,0.06)",
                }}>

                    {/* Panel title */}
                    <div style={{ padding: "28px 28px 20px", borderBottom: "1px solid var(--divider)" }}>
                        <p style={{
                            fontSize: "11px", fontWeight: "700", textTransform: "uppercase",
                            letterSpacing: "0.7px", color: "var(--text-4)", margin: "0 0 8px",
                            fontFamily: "var(--font)",
                        }}>Step 1 of 1</p>
                        <h2 style={{
                            fontFamily: "var(--font-display)", fontWeight: "800",
                            fontSize: "20px", color: "var(--navy)", margin: "0 0 6px",
                            letterSpacing: "-0.3px",
                        }}>Accept &amp; Sign</h2>
                        <p style={{ fontSize: "13px", color: "var(--text-3)", margin: 0, lineHeight: "1.6", fontFamily: "var(--font)" }}>
                            Your registered legal name will be applied as your e-signature on this document.
                        </p>
                    </div>

                    {/* Form body */}
                    <form onSubmit={submit} style={{ flex: 1, padding: "24px 28px", display: "flex", flexDirection: "column", gap: "20px" }}>

                        {/* Full name */}
                        <div>
                            <label style={{
                                display: "block", fontSize: "12px", fontWeight: "700",
                                color: "var(--text-2)", marginBottom: "8px",
                                textTransform: "uppercase", letterSpacing: "0.4px",
                                fontFamily: "var(--font)",
                            }}>Full Legal Name <span style={{ color: "#DE350B" }}>*</span></label>
                            <input
                                type="text"
                                value={fullName}
                                readOnly
                                required
                                style={{
                                    width: "100%", boxSizing: "border-box",
                                    padding: "12px 14px",
                                    borderRadius: "var(--r-md)",
                                    border: "1.5px solid var(--border)",
                                    fontSize: "15px", fontWeight: "500",
                                    color: "var(--navy)",
                                    fontFamily: "'Courier New', 'Courier', monospace",
                                    outline: "none",
                                    background: "var(--bg)",
                                    letterSpacing: "0.3px",
                                    cursor: "default",
                                }}
                            />
                            <p style={{ fontSize: "11px", color: "var(--text-4)", margin: "6px 0 0", fontStyle: "italic", fontFamily: "var(--font)" }}>
                                Your name will be applied as a legally binding e-signature.
                            </p>
                        </div>

                        {/* Agreement checkbox */}
                        <label style={{
                            display: "flex", gap: "12px", alignItems: "flex-start",
                            cursor: "pointer", padding: "16px",
                            background: "var(--bg)", borderRadius: "var(--r-md)",
                            border: `1.5px solid ${agreed ? "var(--blue-accent)" : "var(--border)"}`,
                            transition: "border-color 0.18s",
                        }}>
                            <input
                                type="checkbox"
                                checked={agreed}
                                onChange={e => setAgreed(e.target.checked)}
                                style={{ width: "16px", height: "16px", accentColor: "var(--blue-accent)", flexShrink: 0, marginTop: "2px" }}
                            />
                            <span style={{ fontSize: "13px", color: "var(--text-2)", lineHeight: "1.6", fontFamily: "var(--font)" }}>
                                I have read and understood the Non-Disclosure Agreement and agree to be legally bound by its terms.
                            </span>
                        </label>

                        {/* Legal info notice */}
                        <div style={{
                            background: "var(--surface-2)", borderRadius: "var(--r-md)",
                            padding: "14px 16px", border: "1px solid #bfdbfe",
                            display: "flex", gap: "10px", alignItems: "flex-start",
                        }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: "1px" }}>
                                <circle cx="12" cy="12" r="10" stroke="#0183FF" strokeWidth="1.5"/>
                                <path d="M12 8v4M12 16h.01" stroke="#0183FF" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            <p style={{ fontSize: "12px", color: "#1e40af", margin: 0, lineHeight: "1.6", fontFamily: "var(--font)" }}>
                                This is a legally binding document. Ensure you have the authority to sign on behalf of your organization.
                            </p>
                        </div>

                        {submitError && (
                            <div style={{
                                background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "8px",
                                padding: "10px 14px", fontSize: "13px", color: "#b91c1c", fontWeight: "500",
                            }}>
                                {submitError}
                            </div>
                        )}

                        {/* Push button to bottom */}
                        <div style={{ flex: 1 }} />

                        {/* Submit button */}
                        <button
                            type="submit"
                            disabled={!canSubmit}
                            style={{
                                width: "100%", padding: "15px",
                                borderRadius: "var(--r-pill)", border: "none",
                                background: canSubmit
                                    ? "linear-gradient(135deg, var(--navy) 0%, var(--blue-accent) 100%)"
                                    : "var(--divider)",
                                color: canSubmit ? "#fff" : "var(--text-4)",
                                fontSize: "15px", fontWeight: "700",
                                fontFamily: "var(--font-display)",
                                cursor: canSubmit ? "pointer" : "not-allowed",
                                transition: "all 0.2s",
                                display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                                boxShadow: canSubmit ? "var(--shadow-md)" : "none",
                                letterSpacing: "0.1px",
                            }}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="nda-spinner" style={{ width: "16px", height: "16px", borderWidth: "2px", borderColor: "rgba(255,255,255,0.25)", borderTopColor: "#fff" }} />
                                    Submitting…
                                </>
                            ) : (
                                <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
                                    </svg>
                                    Sign &amp; Request Access
                                </>
                            )}
                        </button>

                        <p style={{ fontSize: "11px", color: "var(--text-4)", textAlign: "center", fontFamily: "var(--font)" }}>
                            Secured by Darwinbox Trust Center &middot; {new Date().getFullYear()}
                        </p>
                    </form>
                </div>
            </div>

            <style>{`
                .nda-spinner {
                    width: 36px; height: 36px;
                    border: 3px solid var(--divider);
                    border-top-color: var(--blue-accent);
                    border-radius: 50%;
                    animation: nda-spin 0.75s linear infinite;
                }
                @keyframes nda-spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    )
}
